import styles from 'css/src/components/common/mentions/ReferenceMention.module.scss';

import React from 'react';
import classNames from 'classnames';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
    LexicalTypeaheadMenuPlugin,
    MenuOption,
    useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import type { TextNode } from 'lexical';
import { $createTextNode } from 'lexical';

import { KIND_LABEL, REFERENCE_KINDS } from './referenceKinds';
import ReferenceRowIcon from './ReferenceRowIcon';
import {
    MentionMenuPortal,
    useEditorIsFocused,
    useScrollSelectedIntoView,
} from './MentionTypeaheadMenu';
import { useVoicePreview } from './useVoicePreview';
import { queryWords, scoreReference } from './scoreReference';
import type { Reference, ReferenceKind } from './ReferenceMentionNode';
import { $createReferenceMentionNode } from './ReferenceMentionNode';

/** Per-kind cap so no single kind can fill the whole menu. */
const MAX_PER_KIND = 5;

const KIND_ORDER: ReferenceKind[] = REFERENCE_KINDS;

const BROWSABLE_KINDS: ReferenceKind[] = ['video', 'blueprint', 'template', 'voice'];

const KIND_SECTION_LABEL: Record<ReferenceKind, string> = {
    video: 'Videos',
    template: 'Templates',
    blueprint: 'Blueprints',
    'brand-kit': 'Brand kits',
    voice: 'Voices',
};

class ReferenceTypeaheadOption extends MenuOption {
    reference: Reference;

    constructor(reference: Reference) {
        super(`${reference.kind}:${reference.id}`);
        this.reference = reference;
    }
}

/**
 * A real option rather than a plain `<li>` so the arrow keys reach it — the
 * typeahead's keyboard navigation only walks the options array.
 */
class SeeAllTypeaheadOption extends MenuOption {
    kind: ReferenceKind;

    constructor(kind: ReferenceKind) {
        super(`see-all:${kind}`);
        this.kind = kind;
    }
}

type MentionMenuOption = ReferenceTypeaheadOption | SeeAllTypeaheadOption;

function isSeeAllOption(option: MentionMenuOption): option is SeeAllTypeaheadOption {
    return option instanceof SeeAllTypeaheadOption;
}

function SeeAllMenuItem({
    index,
    isSelected,
    label,
    onClick,
    onMouseEnter,
}: {
    index: number;
    isSelected: boolean;
    label: string;
    onClick: () => void;
    onMouseEnter: () => void;
}) {
    const itemRef = useScrollSelectedIntoView(isSelected);
    return (
        <li
            ref={itemRef}
            className={classNames(styles['reference-mentions-menu__see-all'], {
                [styles['selected']]: isSelected,
            })}
            role="option"
            aria-selected={isSelected}
            id={`reference-typeahead-item-${index}`}
            // Keep editor focus so the blur-close doesn't beat the click.
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={onMouseEnter}
            onClick={onClick}
        >
            {label}
        </li>
    );
}

function ReferenceMenuItem({
    index,
    isSelected,
    onClick,
    onMouseEnter,
    option,
    isPreviewing,
    onTogglePreview,
}: {
    index: number;
    isSelected: boolean;
    onClick: () => void;
    onMouseEnter: () => void;
    option: ReferenceTypeaheadOption;
    isPreviewing: boolean;
    onTogglePreview: () => void;
}) {
    const { kind, name, detail } = option.reference;
    const itemRef = useScrollSelectedIntoView(isSelected);

    return (
        <li
            ref={itemRef}
            tabIndex={-1}
            className={classNames(styles['reference-menu-item'], {
                [styles['selected']]: isSelected,
            })}
            role="option"
            aria-selected={isSelected}
            id={`reference-typeahead-item-${index}`}
            onMouseEnter={onMouseEnter}
            // Keep focus in the editor so the blur-close doesn't fire before
            // the click selects the option.
            onMouseDown={(event) => event.preventDefault()}
            onClick={onClick}
        >
            <ReferenceRowIcon
                reference={option.reference}
                isPlaying={isPreviewing}
                onTogglePreview={onTogglePreview}
            />
            <span className={styles['reference-menu-item__text']}>
                <span className={styles['reference-menu-item__name']}>{name}</span>
                <span className={styles['reference-menu-item__detail']}>
                    {detail ? `${KIND_LABEL[kind]} · ${detail}` : KIND_LABEL[kind]}
                </span>
            </span>
        </li>
    );
}

interface ReferenceMentionsPluginProps {
    references: Reference[];
    /** `triggerText` is the typed "@query", for in-place replacement on a pick. */
    onSeeAll?: (kind: ReferenceKind, triggerText: string | null) => void;
}

const ReferenceMentionsPlugin: React.FC<ReferenceMentionsPluginProps> = ({
    references,
    onSeeAll,
}) => {
    const [editor] = useLexicalComposerContext();
    const [queryString, setQueryString] = React.useState<string | null>(null);
    const isFocused = useEditorIsFocused();
    const { playingId: previewingId, toggle: togglePreview, stop: stopPreview } = useVoicePreview();
    const canSeeAll = !!onSeeAll;

    const queryResults = React.useMemo(() => {
        const words = queryWords(queryString);
        const scored: { reference: Reference; score: number }[] = [];
        for (const reference of references) {
            const score = scoreReference(reference, words);
            if (score !== null) {
                scored.push({ reference, score });
            }
        }

        // Best match first, so the per-kind cap below keeps the strongest few.
        scored.sort((a, b) => a.score - b.score);
        const matches = scored.map(({ reference }) => reference);

        // Cap per kind rather than overall, so one long list can't crowd the
        // other kinds out of the menu.
        const perKindCount = new Map<ReferenceKind, number>();
        const limited = matches.filter((reference) => {
            const seen = perKindCount.get(reference.kind) ?? 0;
            if (seen >= MAX_PER_KIND) {
                return false;
            }
            perKindCount.set(reference.kind, seen + 1);
            return true;
        });

        // One flat list, since the typeahead's keyboard nav indexes into it,
        // but grouped so rendering can slot a header in at each kind change.
        // "See all" is part of the list, so the arrow keys walk onto it too.
        return KIND_ORDER.flatMap<MentionMenuOption>((kind) => {
            const ofKind = limited.filter((reference) => reference.kind === kind);
            if (ofKind.length === 0) {
                return [];
            }
            const options: MentionMenuOption[] = ofKind.map(
                (reference) => new ReferenceTypeaheadOption(reference),
            );
            if (canSeeAll && BROWSABLE_KINDS.includes(kind)) {
                options.push(new SeeAllTypeaheadOption(kind));
            }
            return options;
        });
    }, [queryString, references, canSeeAll]);

    // `allowWhitespace` keeps the menu open past a space, so multi-word names
    // stay searchable.
    const triggerFn = useBasicTypeaheadTriggerMatch('@', {
        minLength: 0,
        allowWhitespace: true,
    });

    const onSelectOption = React.useCallback(
        (
            selectedOption: MentionMenuOption,
            nodeToReplace: TextNode | null,
            closeMenu: () => void,
        ) => {
            // "See all" opens the browser instead of inserting anything. The
            // typed "@query" stays put — the browser may be dismissed without
            // picking — and its text lets a pick replace it in place, exactly
            // as picking a row here would.
            if (isSeeAllOption(selectedOption)) {
                closeMenu();
                onSeeAll?.(selectedOption.kind, nodeToReplace?.getTextContent() ?? null);
                return;
            }
            editor.update(() => {
                const node = $createReferenceMentionNode(selectedOption.reference);
                if (nodeToReplace) {
                    nodeToReplace.replace(node);
                }
                const spaceNode = $createTextNode(' ');
                node.insertAfter(spaceNode);
                spaceNode.select();
                closeMenu();
            });
        },
        [editor, onSeeAll],
    );

    return (
        <LexicalTypeaheadMenuPlugin
            onQueryChange={setQueryString}
            onSelectOption={onSelectOption}
            options={queryResults}
            triggerFn={triggerFn}
            menuRenderFn={(
                anchorElement,
                { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex },
            ) => (
                <MentionMenuPortal
                    anchorRef={anchorElement}
                    isOpen={isFocused && queryResults.length > 0}
                    optionCount={queryResults.length}
                    className={styles['reference-mentions-menu']}
                    // Read by the editor's Enter handler, so a submit doesn't
                    // steal Enter while the menu is open.
                    testAttribute="data-reference-mentions-menu"
                    onClosed={stopPreview}
                >
                    {queryResults.map((option, index) => {
                        const kind = isSeeAllOption(option) ? option.kind : option.reference.kind;
                        const previous = queryResults[index - 1];
                        // "See all" belongs to the section it closes, so it
                        // never starts one.
                        const startsSection =
                            !isSeeAllOption(option) &&
                            (!previous ||
                                (isSeeAllOption(previous)
                                    ? previous.kind !== kind
                                    : previous.reference.kind !== kind));

                        return (
                            <React.Fragment key={option.key}>
                                {startsSection && (
                                    <li
                                        className={styles['reference-mentions-menu__section']}
                                        role="presentation"
                                    >
                                        {KIND_SECTION_LABEL[kind]}
                                    </li>
                                )}
                                {isSeeAllOption(option) ? (
                                    <SeeAllMenuItem
                                        index={index}
                                        isSelected={selectedIndex === index}
                                        label={`See all ${KIND_SECTION_LABEL[kind].toLowerCase()}`}
                                        onClick={() => selectOptionAndCleanUp(option)}
                                        onMouseEnter={() => setHighlightedIndex(index)}
                                    />
                                ) : (
                                    <ReferenceMenuItem
                                        index={index}
                                        isSelected={selectedIndex === index}
                                        option={option}
                                        isPreviewing={previewingId === option.reference.id}
                                        onTogglePreview={() =>
                                            togglePreview(
                                                option.reference.id,
                                                option.reference.previewUrl,
                                            )
                                        }
                                        onClick={() => {
                                            setHighlightedIndex(index);
                                            selectOptionAndCleanUp(option);
                                        }}
                                        onMouseEnter={() => setHighlightedIndex(index)}
                                    />
                                )}
                            </React.Fragment>
                        );
                    })}
                </MentionMenuPortal>
            )}
        />
    );
};

export default ReferenceMentionsPlugin;
